import { FELLOW_COMPLETION_TYPES, isOneOf, type FellowCompletionType } from './enums.js';

/**
 * Centro's Fellow workspace — the part before `.fellow.app`. The connect form starts with it so a
 * staff member only has to paste their key, and the fixture answers for it by default. It is a
 * default, not a restriction: any valid subdomain is still accepted (server/fellow/client.ts).
 */
export const DEFAULT_FELLOW_WORKSPACE = 'clips4sale';

/**
 * How far back the importer processes meetings on its own (spec §10). A meeting that started within
 * this many days of the sync is summarised (or has Fellow's action items imported) automatically;
 * an older one is imported so it can be seen, and waits for the person to process it. Why: the
 * first sync after connecting would otherwise send every transcript the account ever recorded to
 * the AI provider, paid for and unasked. Both the server rule and the client's copy read this one
 * number, so the words can never drift from the behaviour.
 */
export const AUTO_PROCESS_WINDOW_DAYS = 14;

/** The window in words for copy — "two weeks" for 14 days, "N days" for anything else. */
export function autoProcessWindowLabel(days: number = AUTO_PROCESS_WINDOW_DAYS): string {
  if (days === 14) return 'two weeks';
  return `${days} days`;
}

// --- What Fellow says about an action item (ADR 0014) ------------------------------------------------

/** A single `local@domain` address with no spaces; anything else is not taken as a person's identity. */
export function isEmailShaped(value: string): boolean {
  return /^[^\s@]+@[^\s@]+$/u.test(value);
}

/**
 * Stored assignee addresses (`FellowNoteItem.assigneeEmails`, `todos.fellow_assignees_json`): an array
 * of addresses, lower-cased and deduplicated, or null. A list holding anything but an address reads as
 * unknown (null) rather than as a shorter list. Why all-or-nothing: linked to-dos read the list to tell
 * who is a party to an item and how many owe it, so a partial list would undercount, and unknown fails
 * closed there (nothing links on it).
 */
export function parseAssigneeEmails(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const emails: string[] = [];
  for (const entry of value) {
    if (typeof entry !== 'string' || !isEmailShaped(entry.trim())) return null;
    const email = entry.trim().toLowerCase();
    if (!emails.includes(email)) emails.push(email);
  }
  return emails;
}

/** Fellow's `completion_type`, when it is one Calis knows (`all`, `any`); anything else is unknown. */
export function parseCompletionType(value: unknown): FellowCompletionType | null {
  const text = typeof value === 'string' ? value.trim().toLowerCase() : null;
  return isOneOf(FELLOW_COMPLETION_TYPES, text) ? text : null;
}
